import { Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import { notificationSettingsKeys } from "../../../../../../api/queryKeys";
import ToggleSwitch from "../../../../../../components/ToggleSwitch";
import { field_IDs_mapping } from "../../../fieldMapping";
import { MICROSOFT_TEAMS_TOOL, SLACK_TOOL } from "../GeneralNotificationsPage";
import DaySelector from "./DaySelector";
import FrequencyTypeSelector from "./FrequencyTypeSelector";
import TimeSelector from "./TimeSelector";
import TimezoneSelector from "./TimezoneSelector";

export const frequencyType = {
  DAILY: "daily",
  WEEKLY: "weekly",
};

export default function FrequencyNotificationsSection({
  settingsData,
  setFetchStatus,
  method,
  disabled,
}) {
  const colors = useColors();
  const { updateNotificationSetting } = useAPI();
  const queryClient = useQueryClient();

  const [selectedFrequencyType, setSelectedFrequencyType] = useState(
    settingsData.frequency_type
  );

  const handleUpdate = (method, settingId, newValue) =>
    updateNotificationSetting(method, settingId, newValue).then(() => {
      queryClient.invalidateQueries({
        queryKey: notificationSettingsKeys.all,
      });
    });

  if (Object.keys(settingsData).length === 0) return null;

  // These method don't allow the user controlling the daily/weekly frequency and the selected days
  const methodsWithPartialSettings = [SLACK_TOOL, MICROSOFT_TEAMS_TOOL];

  return (
    <Grid container direction="column" gap={1.5}>
      {!methodsWithPartialSettings.includes(method) && [
        /* Frequency type */
        <Grid item>
          <FrequencyTypeSelector
            method={method}
            setFetchStatus={setFetchStatus}
            selectedFrequencyType={selectedFrequencyType}
            setSelectedFrequencyType={setSelectedFrequencyType}
          />
        </Grid>,
        /* Day */
        <Grid item>
          <Grid container direction="column" gap={0.5}>
            <Grid item>
              <Typography variant="caption" color={colors.text_secondary}>
                Day
              </Typography>
            </Grid>
            <Grid item>
              <DaySelector
                method={method}
                setFetchStatus={setFetchStatus}
                defaultSelectedDays={settingsData.days}
                selectedFrequencyType={selectedFrequencyType}
              />
            </Grid>
          </Grid>
        </Grid>,
      ]}
      {/* Time */}
      <Grid item>
        <Grid container direction="column" gap={0.5}>
          <Grid item>
            <Typography variant="caption" color={colors.text_secondary}>
              Time
            </Typography>
          </Grid>
          <Grid item>
            <TimeSelector
              method={method}
              setFetchStatus={setFetchStatus}
              defaultSelectedTime={settingsData.time}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </Grid>
      {/* Time Zone */}
      <Grid item>
        <Grid container direction="column" gap={0.5}>
          <Grid item>
            <Typography variant="caption" color={colors.text_secondary}>
              Time Zone
            </Typography>
          </Grid>
          <Grid item>
            <TimezoneSelector
              method={method}
              setFetchStatus={setFetchStatus}
              selectedTimezone={settingsData.timezone}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </Grid>
      {/* Ignore manual scans */}
      {!methodsWithPartialSettings.includes(method) && (
        <Grid item marginTop={1}>
          <Grid container gap={2} alignItems="center">
            <Grid item>
              <ToggleSwitch
                defaultValue={false}
                updateFunction={(newValue) =>
                  handleUpdate(
                    method,
                    field_IDs_mapping.FREQUENCY.IGNORE_MANUAL_SCANS,
                    newValue
                  )
                }
                setFetchStatus={setFetchStatus}
                isPermanentlyDisabled={false}
              />
            </Grid>
            <Grid item>
              <Typography variant="body2" color={colors.text_primary}>
                Ignore manual scans
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
